'use client';

import { use, useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { StepsContext } from '../Form/StepsProvider';
import { MEETING_ASIDE_KEY } from '../Form/stepKeys';
type DialogNavigatorProps = {
  dialogOpen: boolean;
  onStepSearchParamCleared?: () => void;
};

/**
 * URL ↔ stepped dialog coordination: sync `s` when the dialog opens with no
 * param; close the dialog when `s` is cleared while open (e.g. browser back);
 * clear `?s=book-meeting` when the step *transitions* to the meeting aside
 * while the dialog is open without an in-flow `goToStep(MEETING_ASIDE_KEY)`
 * (e.g. URL edit mid-session). Deep links that already have `?s=book-meeting`
 * before the dialog opens are left intact.
 */
export function DialogNavigator({
  dialogOpen,
  onStepSearchParamCleared
}: DialogNavigatorProps) {
  const ctx = use(StepsContext);
  const searchParams = useSearchParams();
  const seenSWhileOpenRef = useRef(false);
  const prevActiveStepRef = useRef<string | null>(null);
  const syncActiveStepToUrl = ctx?.syncActiveStepToUrl;
  const clearStepSearchParam = ctx?.clearStepSearchParam;
  const activeStep = ctx?.activeStep;
  const meetingAsideIntentRef = ctx?.meetingAsideIntentRef;
  useLayoutEffect(() => {
    if (!dialogOpen || !syncActiveStepToUrl) return;
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).has('s')) return;
    syncActiveStepToUrl();
  }, [dialogOpen, syncActiveStepToUrl]);
  useLayoutEffect(() => {
    if (!dialogOpen || !activeStep || !clearStepSearchParam) {
      if (!dialogOpen) prevActiveStepRef.current = null;
      return;
    }
    const prev = prevActiveStepRef.current;
    prevActiveStepRef.current = activeStep;
    if (activeStep !== MEETING_ASIDE_KEY) return;
    const transitionedToMeetingAside = prev !== null && prev !== MEETING_ASIDE_KEY;
    if (!transitionedToMeetingAside) return;
    if (meetingAsideIntentRef?.current) return;
    void clearStepSearchParam();
  }, [dialogOpen, activeStep, meetingAsideIntentRef, clearStepSearchParam]);
  useEffect(() => {
    if (!dialogOpen) {
      seenSWhileOpenRef.current = false;
      return;
    }
    if (!onStepSearchParamCleared) return;
    const s = searchParams.get('s');
    if (s) {
      seenSWhileOpenRef.current = true;
      return;
    }
    if (seenSWhileOpenRef.current) {
      seenSWhileOpenRef.current = false;
      onStepSearchParamCleared();
    }
  }, [dialogOpen, searchParams, onStepSearchParamCleared]);
  return null;
}